import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, FileText, Trash2, ExternalLink, Plus, File, Image, AlertCircle } from 'lucide-react';
import { uploadToCloudinary, isCloudinaryConfigured } from '@/lib/cloudinary';
import { cn } from '@/lib/cn';

const DOC_TYPES = [
  'Aadhaar Card',
  'PAN Card',
  'Offer Letter',
  'Experience Certificate',
  'Education Certificate',
  'Passport',
  'Driving Licence',
  'Bank Passbook',
  'Other',
];

const ACCEPT = '.pdf,.jpg,.jpeg,.png,.webp,.doc,.docx';

function DocCard({ doc, onDelete, viewOnly }) {
  const isImage = /\.(jpg|jpeg|png|webp)$/i.test(doc.url || '');
  return (
    <div className="flex items-center gap-3 p-3 rounded-[10px] border border-[var(--border)] bg-[var(--bg-muted)] group">
      <div className="w-9 h-9 rounded-[8px] bg-[var(--bg-card)] border border-[var(--border)] flex items-center justify-center shrink-0">
        {isImage ? <Image size={16} className="text-blue-500"/> : <File size={16} className="text-[var(--text-tertiary)]"/>}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-[var(--text-primary)] truncate">{doc.type}</p>
        <p className="text-[11px] text-[var(--text-tertiary)] truncate">
          {doc.name || 'Uploaded file'} · {doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN') : ''}
        </p>
      </div>
      <div className="flex items-center gap-1 shrink-0">
        <a href={doc.url} target="_blank" rel="noopener noreferrer"
          className="p-1.5 rounded-[6px] hover:bg-[var(--bg-elevated)] text-[var(--text-secondary)] hover:text-[var(--accent)] transition-colors">
          <ExternalLink size={13}/>
        </a>
        {!viewOnly && (
          <button onClick={() => onDelete(doc)} title="Remove"
            className="p-1.5 rounded-[6px] hover:bg-[var(--danger)]/10 text-[var(--text-tertiary)] hover:text-[var(--danger)] transition-colors">
            <Trash2 size={13}/>
          </button>
        )}
      </div>
    </div>
  );
}

function UploadButton({ onUploaded }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('Aadhaar Card');
  const [showPicker, setShowPicker] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('File must be under 10 MB'); return; }
    setUploading(true);
    try {
      if (!isCloudinaryConfigured()) {
        // Dev fallback: create a fake object URL so the UI works without Cloudinary
        const fakeUrl = URL.createObjectURL(file);
        onUploaded({ type: docType, name: file.name, url: fakeUrl, uploaded_at: new Date().toISOString() });
        toast.success(`${docType} saved (local preview — configure Cloudinary to persist)`);
        setShowPicker(false);
        return;
      }
      const url = await uploadToCloudinary(file);
      onUploaded({ type: docType, name: file.name, url, uploaded_at: new Date().toISOString() });
      toast.success(`${docType} uploaded`);
      setShowPicker(false);
    } catch (err) {
      toast.error(err.message || 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  if (!showPicker) {
    return (
      <button type="button" onClick={() => setShowPicker(true)}
        className="flex items-center gap-1.5 text-[12px] font-semibold text-[var(--accent)] hover:underline">
        <Plus size={13}/> Add Document
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select value={docType} onChange={e => setDocType(e.target.value)}
        className="h-9 px-2 rounded-[8px] border border-[var(--border)] text-[13px] bg-[var(--bg-card)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]">
        {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
      </select>
      <button type="button" onClick={() => inputRef.current?.click()} disabled={uploading}
        className={cn(
          'flex items-center gap-1.5 h-9 px-3 rounded-[8px] border border-[var(--border)] text-[13px] font-semibold transition-colors',
          uploading ? 'opacity-50 cursor-wait' : 'hover:bg-[var(--bg-muted)]',
          'text-[var(--text-primary)]'
        )}>
        <Upload size={13}/>
        {uploading ? 'Uploading…' : 'Choose File'}
      </button>
      <button type="button" onClick={() => setShowPicker(false)}
        className="h-9 px-3 rounded-[8px] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--bg-muted)] transition-colors">
        Cancel
      </button>
      <input ref={inputRef} type="file" accept={ACCEPT} onChange={handleFile} className="hidden"/>
    </div>
  );
}

/**
 * Reusable documents section.
 * `viewOnly=true` — read-only (used in employee detail page)
 * `viewOnly=false` — full upload/delete (used in EmployeeForm)
 *
 * `docs` — array of { type, name, url, uploaded_at }
 * `onChange` — called with new docs array (only in edit mode)
 */
export function DocumentsSection({ docs = [], onChange, viewOnly = false }) {
  const handleDelete = (doc) => {
    onChange(docs.filter(d => d !== doc));
  };

  const handleUploaded = (doc) => {
    onChange([...docs, doc]);
  };

  return (
    <div className="space-y-3">
      {!isCloudinaryConfigured() && !viewOnly && (
        <div className="flex items-start gap-2 px-3 py-2 rounded-[8px] bg-amber-50 dark:bg-amber-900/20 border border-amber-200 text-[11px] text-amber-700 dark:text-amber-400">
          <AlertCircle size={13} className="shrink-0 mt-0.5"/>
          <span>Cloudinary not configured — files will preview locally but won't persist. Set <code>VITE_CLOUDINARY_CLOUD_NAME</code> &amp; <code>VITE_CLOUDINARY_UPLOAD_PRESET</code> in .env.development.</span>
        </div>
      )}

      {docs.length === 0 && viewOnly && (
        <p className="text-[13px] text-[var(--text-tertiary)] py-4 text-center">No documents uploaded yet.</p>
      )}

      <div className="space-y-2">
        {docs.map((doc, i) => (
          <DocCard key={i} doc={doc} onDelete={handleDelete} viewOnly={viewOnly}/>
        ))}
      </div>

      {!viewOnly && <UploadButton onUploaded={handleUploaded}/>}
    </div>
  );
}
